import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { customToast } from '../utils/toast'; // Change this import

const EmailVerification = ({ email, onVerified, onBack }) => {
    const [code, setCode] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [timer, setTimer] = useState(600); // 10 minutes in seconds
    const [canResend, setCanResend] = useState(false);

    const inputRefs = useRef([]);

    useEffect(() => {
        // Start countdown timer
        if (timer > 0) {
            const interval = setInterval(() => {
                setTimer((prev) => {
                    if (prev <= 1) {
                        setCanResend(true);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [timer]);

    const handleChange = (index, value) => {
        if (value.length > 1) {
            // If pasting multiple digits
            const digits = value.replace(/\D/g, '').split('');
            const newCode = [...code];
            digits.forEach((digit, i) => {
                if (index + i < 6) {
                    newCode[index + i] = digit;
                }
            });
            setCode(newCode);

            // Focus the next empty input or last input
            const nextEmptyIndex = newCode.findIndex((v, i) => i > index && !v);
            if (nextEmptyIndex !== -1) {
                inputRefs.current[nextEmptyIndex]?.focus();
            } else {
                inputRefs.current[5]?.focus();
            }
        } else if (value === '') {
            // Handle backspace
            const newCode = [...code];
            newCode[index] = '';
            setCode(newCode);

            // Focus previous input on backspace
            if (index > 0) {
                inputRefs.current[index - 1]?.focus();
            }
        } else if (/^\d$/.test(value)) {
            // Handle single digit
            const newCode = [...code];
            newCode[index] = value;
            setCode(newCode);

            // Auto-focus next input
            if (index < 5 && value) {
                inputRefs.current[index + 1]?.focus();
            }

            // If all digits are filled, auto-verify
            if (index === 5 || (index < 5 && newCode.every(d => d))) {
                setTimeout(() => handleVerify(), 300);
            }
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text');
        const digits = pastedData.replace(/\D/g, '').split('').slice(0, 6);

        const newCode = [...code];
        digits.forEach((digit, i) => {
            newCode[i] = digit;
        });
        setCode(newCode);

        // Focus appropriate input
        if (digits.length === 6) {
            inputRefs.current[5]?.focus();
            setTimeout(() => handleVerify(), 300);
        } else {
            inputRefs.current[digits.length]?.focus();
        }
    };

    const handleVerify = async () => {
        const verificationCode = code.join('');
        if (verificationCode.length !== 6) {
            customToast.error('Please enter the 6-digit verification code');
            return;
        }

        console.log('Sending verification:', { email, code: verificationCode }); // Add this log

        setLoading(true);
        try {
            const response = await api.post('/verify-email.php', {
                email: email,
                code: verificationCode
            });

            console.log('Verify response:', response); // Add this log

            if (response.success) {
                customToast.success('Email verified successfully!');
                onVerified();
            } else {
                customToast.error(response.message);
                setCode(['', '', '', '', '', '']);
                inputRefs.current[0]?.focus();
            }
        } catch (error) {
            console.error('Verify error details:', error); // Enhanced error log
            console.error('Error response:', error.response); // Log the full error response
            customToast.error(error.response?.data?.message || error.message || 'Verification failed');
            setCode(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
        } finally {
            setLoading(false);
        }
    };

    const handleResendCode = async () => {
        setResendLoading(true);
        try {
            const response = await api.post('/send-verification.php', { email });

            if (response.data.success) {
                customToast.success('New verification code sent!'); // Changed to customToast.success
                setTimer(600);
                setCanResend(false);
                setCode(['', '', '', '', '', '']);
                inputRefs.current[0]?.focus();
            } else {
                customToast.error(response.data.message); // Changed to customToast.error
            }
        } catch (error) {
            customToast.error(error.response?.data?.message || 'Failed to resend code'); // Changed to customToast.error
        } finally {
            setResendLoading(false);
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <h2 className="text-center text-3xl font-extrabold text-gray-900">
                    Verify your email
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600">
                    We've sent a 6-digit verification code to<br />
                    <span className="font-medium text-indigo-600">{email}</span>
                </p>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                    <div className="space-y-6">
                        {/* Verification code inputs */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-4">
                                Enter verification code
                            </label>
                            <div className="flex justify-between gap-2" onPaste={handlePaste}>
                                {code.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={(el) => (inputRefs.current[index] = el)}
                                        type="text"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleChange(index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                        className="w-12 h-12 text-center text-2xl font-bold border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        disabled={loading}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Timer and resend */}
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-600">
                                {timer > 0 ? (
                                    <>Code expires in: <span className="font-medium">{formatTime(timer)}</span></>
                                ) : (
                                    <span className="text-red-600">Code expired</span>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={handleResendCode}
                                disabled={!canResend || resendLoading}
                                className="text-sm font-medium text-indigo-600 hover:text-indigo-500 disabled:text-gray-400 disabled:cursor-not-allowed"
                            >
                                {resendLoading ? 'Sending...' : 'Resend code'}
                            </button>
                        </div>

                        {/* Verify button */}
                        <div>
                            <button
                                onClick={handleVerify}
                                disabled={loading || code.some(d => !d) || timer === 0}
                                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Verifying...' : 'Verify Email'}
                            </button>
                        </div>

                        {/* Back button */}
                        <div className="text-center">
                            <button
                                onClick={onBack}
                                className="text-sm text-gray-600 hover:text-gray-500"
                            >
                                ← Back to registration
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmailVerification;